import { fullDate, setUserZone } from '../../lib/time.js'
import { devicePush, disablePush, enablePush } from '../../lib/push.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- tpl-settings ---
let __fez_component_0;
{
const PUSH_TEXT = {
  loading: 'Checking this device...',
  unsupported: 'This browser cannot show notifications from MailCog.',
  blocked: 'Notifications are blocked for this site in the browser\'s settings. Allow them there, then reload this page.',
  off: 'Off on this device: reminders do not show here.',
  on: 'On: reminders show on this device, even with MailCog closed.',
}
__fez_component_0 = class {
  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.key = null
    this.state.created = null
    this.state.busy = ''
    this.state.zoneError = ''
    this.state.leadsError = ''
    this.state.savingLeads = false
    this.state.savingNextUp = false
    this.state.push = 'loading'
    this.state.pushError = ''
    this.state.pushBusy = ''
  }

  async saveZone(zone) {
    try {
      const me = await window.App.api('user/update', { time_zone: zone }, { silent: true })
      this.globalState.me = me
      setUserZone(me.time_zone)
      this.state.zoneError = ''
      window.Toast?.success('Time zone saved')
    } catch (error) {
      this.state.zoneError = error.details?.time_zone || error.message
    }
  }

  async saveLeads(list) {
    this.state.savingLeads = true
    try {
      this.globalState.me = await window.App.api('user/update', { reminders: list }, { silent: true })
      this.state.leadsError = ''
      window.Toast?.success('Default reminders saved')
    } catch (error) {
      this.state.leadsError = error.details?.reminders || error.message
    } finally {
      this.state.savingLeads = false
    }
  }

  async setNextUp(on) {
    this.state.savingNextUp = true
    try {
      this.globalState.me = await window.App.api('user/update', { next_up: on }, { silent: true })
      window.Toast?.success(on ? 'Next up shows above the Inbox' : 'Next up is hidden')
    } catch (error) {
      window.Toast?.error(error.message)
    } finally {
      this.state.savingNextUp = false
    }
  }

  async loadPush() {
    try {
      const device = await devicePush()
      this.state.push = !device.supported ? 'unsupported' : device.permission === 'denied' ? 'blocked' : device.subscribed && device.serverSubscribed ? 'on' : 'off'
      this.state.pushError = ''
    } catch (error) {
      this.state.push = 'off'
      this.state.pushError = error.message
    }
  }

  async togglePush() {
    const on = this.state.push === 'on'
    this.state.pushBusy = 'toggle'
    try {
      if (on) await disablePush()
      else await enablePush()
      await this.loadPush()
      if (this.state.push === 'on') window.Toast?.success('Notifications on for this device')
      else if (on) window.Toast?.success('Notifications off for this device')
    } catch (error) {
      this.state.pushError = error.details?.endpoint || error.message
    } finally {
      this.state.pushBusy = ''
    }
  }

  async testPush() {
    this.state.pushBusy = 'test'
    try {
      await window.App.api('push/test', {}, { silent: true })
      this.state.pushError = ''
      window.Toast?.success('Test sent: it shows in a moment')
    } catch (error) {
      this.state.pushError = error.message
    } finally {
      this.state.pushBusy = ''
    }
  }

  onMount() {
    this.load()
    if (this.globalState.me && !this.globalState.sudo) this.loadPush()
  }

  async load() {
    try {
      const data = await window.App.api('user/key', {}, { silent: true })
      this.state.key = data.key
      this.state.error = ''
    } catch (error) {
      this.state.error = error.message
    } finally {
      this.state.loading = false
    }
  }

  async create() {
    if (this.state.key) {
      const ok = await window.Dialog.confirm({ title: 'Replace your API key?', text: 'The current key stops working at once, in every script and MCP client that uses it.', confirm: 'Replace key', danger: true })
      if (!ok) return
    }
    this.state.busy = 'create'
    try {
      const created = await window.App.api('user/key_create')
      this.state.created = created
      this.state.key = created.key
    } finally {
      this.state.busy = ''
    }
  }

  async remove() {
    const ok = await window.Dialog.confirm({ title: 'Delete your API key?', text: 'Scripts and MCP clients using it stop working at once.', confirm: 'Delete key', danger: true })
    if (!ok) return
    this.state.busy = 'delete'
    try {
      await window.App.api('user/key_delete')
      this.state.created = null
      this.state.key = null
    } finally {
      this.state.busy = ''
    }
  }

  beforeRender() {
    this.state.device = !this.globalState.sudo
    this.state.pushText = PUSH_TEXT[this.state.push]
    this.state.since = this.state.key ? `Created ${fullDate(this.state.key.created_at)}` : ''
    if (this.state.created) {
      const origin = window.location.origin
      const token = this.state.created.token
      this.state.curl = `curl -X POST ${origin}/api/auth/me -H 'Authorization: Bearer ${token}'`
      this.state.mcp = JSON.stringify({ mcpServers: { mailcog: { type: 'http', url: `${origin}/mcp`, headers: { Authorization: `Bearer ${token}` } } } }, null, 2)
    }
  }

  CSS = `:fez {
display:block;.settings-page{max-width:var(--content-width);margin:0 auto;padding:var(--space-10) var(--space-6) var(--space-12)}h1{font-size:var(--text-2xl);letter-spacing:-0.02em}h2{margin:var(--space-8) 0 var(--space-3);color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-bold);letter-spacing:0.06em;text-transform:uppercase}.settings-sub{margin-top:var(--space-2);color:var(--color-text-muted)}.key-lead{margin-bottom:var(--space-4);color:var(--color-text-muted);font-size:var(--text-sm)}.key-row{display:flex;align-items:center;gap:var(--space-3)}.key-icon{display:grid;place-items:center;width:40px;height:40px;flex:none;border-radius:var(--radius-lg);background:var(--color-bg-subtle);color:var(--color-text-muted)}.key-info{flex:1;min-width:0;line-height:var(--leading-tight)}.key-prefix{display:block;font-family:var(--font-mono);font-weight:var(--weight-semibold)}.key-since{color:var(--color-text-muted);font-size:var(--text-sm)}.key-fresh{display:grid;grid-template-columns:minmax(0,1fr);gap:var(--space-3);margin-top:var(--space-5);padding-top:var(--space-5);border-top:1px solid var(--color-border)}.push-block{margin-top:var(--space-5);padding-top:var(--space-5);border-top:1px solid var(--color-border)}.push-title{display:block;font-weight:var(--weight-semibold)}.push-error{display:block;margin-top:var(--space-2);color:var(--color-danger);font-size:var(--text-sm)}.key-note{color:var(--color-text-muted);font-size:var(--text-sm)} :mobile {.settings-page{padding:var(--space-6) var(--space-4) var(--space-10)}.key-row{flex-wrap:wrap}}
}`

  HTML = `<div class="settings-page">
<h1>Settings</h1>
<p class="settings-sub">Your account.</p>
{#if globalState.me}
<h2>Picture</h2>
<div class="ui-card pad-lg">
<p class="key-lead">Shown next to your name across MailCog. Change your name and username from your avatar's menu.</p>
<avatar-picker></avatar-picker>
</div>
{/if}
<h2>Time zone</h2>
<div class="ui-card pad-lg">
<p class="key-lead">Every time in the app is shown in this zone, and the times you pick are read in it.</p>
<input-time-zone name="time_zone" label="Time zone" value={globalState.me?.time_zone || ''} error={state.zoneError} :on_change="(zone) => fez.saveZone(zone)"></input-time-zone>
</div>
{#if globalState.me}
<h2>Reminders</h2>
<div class="ui-card pad-lg">
<p class="key-lead">Every new event starts with these reminders; change them on any event under Notify me. They show as notifications on the devices where you turn them on.</p>
<calendar-reminders label="Default reminders" :value="globalState.me.reminders || []" error={state.leadsError} :disabled="state.savingLeads" :on_change="(list) => fez.saveLeads(list)"></calendar-reminders>
{#if state.device}
<div class="push-block">
<div class="key-row">
<span class="key-icon">
{#if state.push === 'on'}
<ui-icon name="bell-ringing" size="20"></ui-icon>
{:else}
<ui-icon name="bell-slash" size="20"></ui-icon>
{/if}
</span>
<div class="key-info">
<span class="push-title">Notifications on this device</span>
<span class="key-since">{state.pushText}</span>
</div>
{#if state.push === 'on'}
<ui-button size="sm" icon="paper-plane-tilt" label="Send a test" loading={state.pushBusy === 'test'} onclick="fez.testPush()"></ui-button>
<ui-button size="sm" variant="ghost" label="Turn off" loading={state.pushBusy === 'toggle'} onclick="fez.togglePush()"></ui-button>
{:else if state.push === 'off'}
<ui-button size="sm" variant="primary" icon="bell" label="Turn on" loading={state.pushBusy === 'toggle'} onclick="fez.togglePush()"></ui-button>
{/if}
</div>
{#if state.pushError}<span class="push-error" role="alert">{state.pushError}</span>{/if}
</div>
{/if}
</div>
<h2>Mail app</h2>
<div class="ui-card pad-lg">
<div class="key-row">
<span class="key-icon"><ui-icon name="calendar-dots" size="20"></ui-icon></span>
<div class="key-info">
<span class="push-title">Next up</span>
<span class="key-since">{globalState.me.next_up ? 'Shown above the Inbox: today and the next two days of your calendar.' : 'Hidden from the Inbox.'}</span>
</div>
{#if globalState.me.next_up}
<ui-button size="sm" variant="ghost" icon="eye-slash" label="Hide" loading={state.savingNextUp} onclick="fez.setNextUp(false)"></ui-button>
{:else}
<ui-button size="sm" variant="primary" icon="eye" label="Show" loading={state.savingNextUp} onclick="fez.setNextUp(true)"></ui-button>
{/if}
</div>
</div>
{/if}
<h2>API key</h2>
{#if state.error}
<ui-empty icon="warning-circle" title="Could not load your API key" text={state.error}></ui-empty>
{:else if state.loading}
<ui-spinner></ui-spinner>
{:else}
<div class="ui-card pad-lg">
<p class="key-lead">The key acts as you on the API and for AI assistants (MCP): everything you can do here, with your rights. Keep it secret.</p>
<div class="key-row">
<span class="key-icon"><ui-icon name="key" size="20"></ui-icon></span>
{#if state.key}
<div class="key-info">
<span class="key-prefix">{state.key.prefix}...</span>
<span class="key-since">{state.since}</span>
</div>
<ui-button size="sm" icon="arrows-clockwise" label="New key" loading={state.busy === 'create'} onclick="fez.create()"></ui-button>
<ui-button size="sm" variant="ghost" icon="trash" title="Delete key" aria-label="Delete key" loading={state.busy === 'delete'} onclick="fez.remove()"></ui-button>
{:else}
<div class="key-info"><span class="key-since">No API key yet.</span></div>
<ui-button size="sm" variant="primary" icon="key" label="Create key" loading={state.busy === 'create'} onclick="fez.create()"></ui-button>
{/if}
</div>
{#if state.created}
<div class="key-fresh">
<span class="key-note">Copy the key now; it is not shown again.</span>
<ui-copy label="API key" value={state.created.token}></ui-copy>
<ui-copy label="Try it" value={state.curl}></ui-copy>
<ui-copy label="MCP client settings" value={state.mcp} multiline></ui-copy>
</div>
{/if}
</div>
{/if}
</div>`
};
}

Fez('tpl-settings', __fez_component_0);

export default __fez_component_0;
